import {
  BitkitDrawer,
  BitkitUIHeader,
  IconBell,
  IconNews,
  IconPlusCircle,
  IconQuestionCircle,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Portal } from '@chakra-ui/react/portal';
import { datadogRum } from '@datadog/browser-rum';
import { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import { HEADER_HEIGHT } from '@/constants';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import useChangelog from '@/hooks/useChangelog';
import { getColorModePreference, setColorModePreference } from '@/utils/colorMode';
import { clearLoginAttemptMarkers, wasLoginAttempted } from '@/utils/loginTracking';
import { invokeReoSignInEventWithEmail } from '@/utils/reo';

import { UserContext } from '../../../../models/User';
import { GlobalPropsContext } from '../../GlobalPropsContext';
import AskCIConfigExpertButton from './AskAIButton/AskCIConfigExpertButton';
import AccountDrawer from './Drawer/AccountDrawer';
import ChangelogDrawer from './Drawer/ChangelogDrawer';
import HelpDrawer from './Drawer/HelpDrawer';
import NotificationsDrawer from './Drawer/NotificationsDrawer';
import SearchDrawer from './Drawer/SearchDrawer';
import SearchResults from './Search/SearchResults';
import useSearch from './useSearch';

type UIHeaderProps = {
  children: ReactNode;
  helpDrawerContent?: ReactNode;
  marginBlockStart: string;
  onNavOpenClick: () => void;
};

const onSuccessfulLogin = async (userContext: UserContext) => {
  // Reo tracking
  try {
    await invokeReoSignInEventWithEmail(userContext.email);
  } catch (error) {
    datadogRum.addError('Reo tracking failed', { error });
  }
};

const UIHeader = ({ children, helpDrawerContent, marginBlockStart, onNavOpenClick }: UIHeaderProps) => {
  const { isMobile } = useResponsive();

  const { user, account, featureFlags } = useContext(GlobalPropsContext);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [changeLogRead, setChangeLogRead] = useState(false);
  const [drawerContent, setDrawerContent] = useState<keyof typeof drawerTitles>();

  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { query, setQuery, appList, isLoading } = useSearch();

  useEffect(() => {
    if (user && wasLoginAttempted()) {
      onSuccessfulLogin(user);
      clearLoginAttemptMarkers();
    }
  }, [user]);

  const drawerTitles = {
    help: 'Help',
    notifications: 'Notifications & activity',
    account: 'Signed in as',
    search: 'Search in your workspaces',
    changelog: 'Changelog',
  };

  const toggleDrawer = (name: keyof typeof drawerTitles) => {
    if (drawerContent === name) {
      closeDrawers();
    } else {
      setDrawerContent(name);
    }
  };

  const closeDrawers = () => {
    setDrawerContent(undefined);
  };

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setQuery('');
  }, [setQuery]);

  // Below the tablet breakpoint the header draws no field of its own: the button is a plain trigger
  // and the search is a drawer instead.
  const handleSearchOpenChange = (open: boolean) => {
    if (isMobile) {
      toggleDrawer('search');
      return;
    }

    if (open) {
      closeDrawers();
      setIsSearchOpen(true);
    } else {
      closeSearch();
    }
  };

  // The header dismisses the field for presses on its own controls and nothing else, by design. A
  // press anywhere else on the page has to close it too, or the results panel stays over the page.
  useOnClickOutside(searchRef, () => {
    if (isSearchOpen) {
      closeSearch();
    }
  });

  const { hasUnreadTopics: changelogHasUnreadTopics } = useChangelog();

  const searchResults = !isMobile && isSearchOpen && query && !isLoading ? appList : undefined;

  return (
    <>
      <BitkitUIHeader
        banner={children}
        className="platform-header"
        insetBlockStart={0}
        insetInline={0}
        leading={
          account ? (
            <BitkitUIHeader.NavButton
              name={account.name}
              onClick={() => {
                onNavOpenClick();
                closeDrawers();
              }}
              src={account.avatarUrl || undefined}
            />
          ) : (
            <BitkitUIHeader.Control href="/create-workspace" icon={IconPlusCircle} label="Create workspace" />
          )
        }
        marginBlockStart={marginBlockStart}
        position="fixed"
        zIndex={300}
      >
        <AskCIConfigExpertButton />
        {account && (
          <BitkitUIHeader.Search
            className="platform-shell-search"
            inputRef={searchInputRef}
            label="Search projects"
            onOpenChange={handleSearchOpenChange}
            onValueChange={setQuery}
            open={isSearchOpen}
            placeholder="Search projects in your workspaces..."
            ref={searchRef}
            selected={isMobile ? drawerContent === 'search' : undefined}
            value={query}
          />
        )}
        {!isMobile && (
          <BitkitUIHeader.Control
            badge={!changeLogRead && changelogHasUnreadTopics}
            icon={IconNews}
            label="Changelog"
            onClick={() => {
              if (drawerContent !== 'changelog') segmentTrack('Changelog Drawer Opened');
              toggleDrawer('changelog');
              setChangeLogRead(true);
            }}
            selected={drawerContent === 'changelog'}
          />
        )}
        {!isMobile && (
          <BitkitUIHeader.Control
            icon={IconQuestionCircle}
            label="Help"
            onClick={() => toggleDrawer('help')}
            selected={drawerContent === 'help'}
          />
        )}
        {!isMobile && account && (
          <BitkitUIHeader.Control
            icon={IconBell}
            label="Notifications"
            onClick={() => toggleDrawer('notifications')}
            selected={drawerContent === 'notifications'}
          />
        )}
        <BitkitUIHeader.Account
          className="platform-shell-account"
          name={user?.username || ''}
          onClick={() => toggleDrawer('account')}
          selected={drawerContent === 'account'}
          src={user?.avatarUrl || undefined}
        />
        {featureFlags.user.enableDarkMode && (
          <BitkitUIHeader.ModeSwitch defaultValue={getColorModePreference()} onValueChange={setColorModePreference} />
        )}
      </BitkitUIHeader>

      {searchResults && (
        <SearchResults
          anchorRef={searchRef}
          apps={searchResults}
          onFocusReset={() => searchInputRef.current?.focus()}
        />
      )}

      <BitkitDrawer.Root open={!!drawerContent} onOpenChange={({ open }) => !open && closeDrawers()}>
        <Portal>
          <BitkitDrawer.Backdrop insetBlockStart={HEADER_HEIGHT} />
          <BitkitDrawer.Positioner insetBlockStart={HEADER_HEIGHT}>
            <BitkitDrawer.Content paddingBlock="24" paddingInline="0">
              <BitkitDrawer.Header paddingBlockStart="4" paddingBlockEnd="0" paddingInline="24">
                <BitkitDrawer.Title>{drawerContent ? drawerTitles[drawerContent] : ''}</BitkitDrawer.Title>
                <BitkitDrawer.CloseButton />
              </BitkitDrawer.Header>
              <BitkitDrawer.Body padding="0" paddingBlockStart="8">
                {drawerContent === 'search' && <SearchDrawer />}
                {drawerContent === 'account' && user && <AccountDrawer user={user} />}
                {drawerContent === 'notifications' && <NotificationsDrawer />}
                {drawerContent === 'help' && <HelpDrawer>{helpDrawerContent}</HelpDrawer>}
                {drawerContent === 'changelog' && <ChangelogDrawer />}
              </BitkitDrawer.Body>
            </BitkitDrawer.Content>
          </BitkitDrawer.Positioner>
        </Portal>
      </BitkitDrawer.Root>
    </>
  );
};

export default UIHeader;
